import { Button, Input, Space, Tooltip } from 'antd';
import { cloneDeep } from 'lodash';
import React from 'react';

import Icon from '@ant-design/icons';
import PlusIcon from '@icons/emp/plus_icon.svg';
import Chain from '@icons/funnel/chain.svg';
import TreeIcon from '@icons/sankey/tree-icon.svg';
import { IEventJourney } from '@src/emp-3.0/components/Sankey/Configurations/EventJourney/typed';
import {
  CompactDualSelectWithRequest,
  DeleteIcon,
} from '@src/emp-3.0/components/Sankey/components/CompactDualSelect';
import { DimensionValueSelect } from '@src/emp-3.0/components/Sankey/components/CompactDualSelect/styled';
import {
  isEventBreakdown,
  isLandingEvent,
  isSubsetArray,
} from '@src/emp-3.0/components/Sankey/utils/check';
import { IEventBreakdown, IEventFilter, ILandingEvent } from '@src/emp-3.0/types';

const AddDimensionTip = 'Add Dimension Filter';
const EventJourney: React.FC<IEventJourney> = ({
  interval,
  filters,
  eventData,
  showDelete = true,
  loading,
  eventOptions = [],
  fixEvent = [],
  compactDualSelectProps = {},
  onDeleteEvent,
  onChange,
  onAddDimension,
  addDimensionButtonProps = { show: true, text: 'Add Dimension' },
  compactTitle = 'Event: ',
  showIconStyle,
  style,
  minWidth,
}) => {
  const {
    dimensionSelectProps,
    dimensionValueSelectProps,
    style: compactDualSelectStyle,
    onDelete,
    ...restCompactDualSelectProps
  } = compactDualSelectProps;

  const eventFilters: IEventFilter[][] = isLandingEvent(eventData)
    ? eventData?.filters
    : isEventBreakdown(eventData)
    ? eventData?.breakdowns
    : [];

  const isFixBreakdownEvent = fixEvent.length > 0 && isEventBreakdown(fixEvent[0]);

  const dimensionNameOptions = dimensionSelectProps?.options?.map((item) => {
    if (eventFilters.some((andFilter) => andFilter[0].field === item.value)) {
      return {
        ...item,
        disabled: true,
      };
    }
    return item;
  });

  const handleEventChange = (value) => {
    const cloneEventData = cloneDeep(eventData);
    cloneEventData.event_name = value;
    onChange?.(cloneEventData);
  };

  const handleChange = (value, andIndex) => {
    const cloneEventData: ILandingEvent | IEventBreakdown = cloneDeep(eventData);
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters[andIndex] = value;
      onChange?.(cloneEventData);
    } else if (isEventBreakdown(cloneEventData)) {
      cloneEventData.breakdowns[andIndex] = value;
      onChange?.(cloneEventData);
    }
    onDelete?.();
  };

  const handleDelete = (value, removeAndIndex) => {
    const cloneEventData: ILandingEvent | IEventBreakdown = cloneDeep(eventData);
    if (isLandingEvent(cloneEventData)) {
      cloneEventData.filters = cloneEventData.filters.filter(
        (_, index) => index !== removeAndIndex,
      );
      onChange?.(cloneEventData);
    } else if (isEventBreakdown(cloneEventData)) {
      cloneEventData.breakdowns = cloneEventData.breakdowns.filter(
        (_, index) => index !== removeAndIndex,
      );
      onChange?.(cloneEventData);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
      <div style={{ display: 'flex', width: '100%' }}>
        <Space.Compact style={{ flex: 1 }}>
          <Input
            style={{
              width: 76,
              background: '#e6edff',
              color: 'rgba(0,0,0,0.88)',
              cursor: 'default',
              borderRadius: '2px',
              flexShrink: 0,
            }}
            value={compactTitle}
            disabled
          />
          <DimensionValueSelect
            minWidth={minWidth}
            style={{ maxWidth: showDelete ? 250 : 280 }}
            loading={loading}
            value={eventData.event_name}
            options={eventOptions}
            filterOption={(inputValue, option = []) =>
              (option?.label ?? '').toLowerCase().includes(inputValue.toLowerCase())
            }
            showSearch
            showRightBorder={!showDelete}
            onChange={(value) => {
              handleEventChange(value);
            }}
          />
          {showDelete && <DeleteIcon onClick={onDeleteEvent} />}
        </Space.Compact>
        <div style={{ flexShrink: 0, marginLeft: 4, width: 32 }}>
          {showIconStyle && !eventFilters.length && (
            <Tooltip title={AddDimensionTip}>
              <Button
                type="default"
                icon={<Icon component={Chain} />}
                disabled={!eventData?.event_name}
                onClick={onAddDimension}
              />
            </Tooltip>
          )}
        </div>
      </div>

      <div style={{ display: 'flex' }}>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {eventFilters.map((andFilters, andIndex) => {
            const field = andFilters[0].field;
            const value = andFilters[0].value;
            const findEvent = fixEvent.find((event) => event.event_name === eventData.event_name);
            const isFixBreakdownEvent = findEvent && isEventBreakdown(findEvent);

            const notShowDelete =
              isEventBreakdown(eventData) &&
              isFixBreakdownEvent &&
              findEvent.breakdowns.some(
                (andItem) => field === andItem[0].field && isSubsetArray(andItem[0].value, value),
              );

            let disabledValueOptions: string[] = [];
            isFixBreakdownEvent &&
              findEvent.breakdowns.forEach((andItem) => {
                if (andItem[0].field === field) {
                  disabledValueOptions = andItem[0].value;
                }
              });
            return (
              <CompactDualSelectWithRequest
                key={`${field}_${andIndex}`}
                disabledValueOptions={disabledValueOptions}
                showDelete={!notShowDelete}
                interval={interval}
                filters={filters}
                dimensionSelectProps={{
                  value: field,
                  ...dimensionSelectProps,
                  disabled: notShowDelete || dimensionSelectProps?.disabled,
                  showArrow: !notShowDelete || dimensionSelectProps?.showArrow,
                  options: dimensionNameOptions,
                  onChange: (value, ...rest) => {
                    handleChange([{ ...andFilters[0], field: value, value: [] }], andIndex);
                    dimensionSelectProps?.onChange?.(value, ...rest);
                  },
                  style: { minWidth: 80, maxWidth: 120, ...dimensionSelectProps?.style },
                }}
                dimensionValueSelectProps={{
                  value: value,
                  ...dimensionValueSelectProps,
                  onChange: (value: string | string[], ...rest) => {
                    if (typeof value === 'string') {
                      handleChange([{ ...andFilters[0], value: [value] }], andIndex);
                      dimensionValueSelectProps?.onChange?.([value], ...rest);
                    } else {
                      handleChange([{ ...andFilters[0], value: value }], andIndex);
                      dimensionValueSelectProps?.onChange?.(value, ...rest);
                    }
                  },
                  style: { ...dimensionValueSelectProps?.style },
                }}
                style={{
                  paddingLeft: 16,
                  ...compactDualSelectStyle,
                  marginBottom: 8,
                  width: '100%',
                }}
                prefix={
                  <Icon
                    component={TreeIcon}
                    style={{
                      backgroundColor: '#f5f5f5',
                      fontSize: '10px',
                      border: '1px solid #d9d9d9',
                      borderRight: 0,
                      paddingLeft: '8px',
                      height: 32,
                      display: 'inline-flex',
                    }}
                  />
                }
                onDelete={() => handleDelete(andFilters, andIndex)}
                {...restCompactDualSelectProps}
              />
            );
          })}
        </div>
        <div
          style={{
            flexShrink: 0,
            marginLeft: 4,
            width: 32,
            display: 'flex',
            alignItems: 'flex-end',
            paddingBottom: '8px',
          }}
        >
          {addDimensionButtonProps.show && showIconStyle && eventFilters.length ? (
            <Tooltip title={AddDimensionTip}>
              <Button
                style={{ flexShrink: 0, marginLeft: 4 }}
                type="link"
                icon={<Icon component={PlusIcon} />}
                disabled={!eventData?.event_name}
                onClick={onAddDimension}
              />
            </Tooltip>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EventJourney;
